import { z } from 'zod';

import { apiGet, apiPost } from '../client';
import {
  ChargeCreatedSchema,
  ChargeListResponseSchema,
  ChargeRecordSchema,
  type ChargeCreated,
  type ChargeListResponse,
  type ChargePaymentMethod,
  type ChargeRecord,
} from '../schemas';

/** Mirrors the backend's own default page size (business-portal-metrics.ts, see billing.ts). */
const DEFAULT_CHARGES_PAGE_SIZE = 20;

export type ChargeItemInput =
  | { serviceId: string; quantity: number }
  | { name: string; unitPriceCents: number; quantity: number };

export type ChargeDiscountInput = {
  type: 'percent' | 'fixed';
  value: number;
};

export type CreateChargeInput = {
  items: ChargeItemInput[];
  /** Discount XOR offerId: never send both (docs/CONCEPTS.md — Descuento). */
  discount?: ChargeDiscountInput;
  offerId?: string;
  paymentMethod: ChargePaymentMethod;
  /** Required for Banco Aliado and Transferencia (src/lib/paymentMethods.ts), trimmed. */
  paymentReference?: string;
  bookingId?: string;
  note?: string;
  /** When the money actually changed hands; omitted = now. Never in the future. */
  chargedAt?: string;
  /** Received amount when it is an abono (partial); omitted = the whole total. */
  amountPaidCents?: number;
  /** The client's own computed total (src/lib/chargeTotals.ts); a mismatch comes back as 409 total_mismatch. */
  expectedTotalCents: number;
};

/** The Cobro, plus its Factura (or why there is none) when "Emitir factura al cobrar" is on. */
export function createCharge(input: CreateChargeInput, idempotencyKey: string): Promise<ChargeCreated> {
  return apiPost('/v1/business/charges', ChargeCreatedSchema, input, idempotencyKey);
}

/** OWNER-only per the contract; the Cobrar flow itself never lists charges. */
export function listCharges(
  cursor?: string,
  limit = DEFAULT_CHARGES_PAGE_SIZE,
): Promise<ChargeListResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set('cursor', cursor);
  return apiGet(`/v1/business/charges?${params.toString()}`, ChargeListResponseSchema);
}

/** Anular a manual payment (owner-only, with reason). */
export function voidCharge(
  chargeId: string,
  reason: string,
  idempotencyKey: string,
): Promise<ChargeRecord> {
  return apiPost(
    `/v1/business/charges/${encodeURIComponent(chargeId)}/void`,
    ChargeRecordSchema,
    { reason },
    idempotencyKey,
  );
}

/** The package Usar paquete would spend on a visit: the one the app would take. */
export const PackOptionSchema = z.object({
  subscriptionId: z.string(),
  planName: z.string(),
  kind: z.enum(['pack', 'unlimited']),
  /** Credits left; null for an unlimited pass. */
  creditsLeft: z.number().int().nonnegative().nullable(),
  expiresAt: z.string(),
});
export type PackOption = z.infer<typeof PackOptionSchema>;

export const BookingBalanceSchema = z.object({
  bookingId: z.string(),
  priceCents: z.number(),
  /** Received toward the visit's service; an extra's plata is not. */
  paidCents: z.number(),
  balanceCents: z.number(),
  /** Whether a Cobro already charged the service. Absent on older APIs. */
  serviceCharged: z.boolean().optional(),
  /** A credit of the client's package pays the service, so it owes nothing for it. Absent on older APIs. */
  packCovered: z.boolean().optional(),
  /** The package Usar paquete would spend right now; null when none fits. Absent on older APIs. */
  packAvailable: PackOptionSchema.nullable().optional(),
});
export type BookingBalance = z.infer<typeof BookingBalanceSchema>;

/** What a visit still owes after its Cobros; owner-only on the API. */
export function getBookingBalance(bookingId: string): Promise<BookingBalance> {
  return apiGet(`/v1/business/bookings/${encodeURIComponent(bookingId)}/balance`, BookingBalanceSchema);
}

/** Usar paquete: one credit of the client's package pays the visit. Answers its balance after. */
export function spendBookingPack(bookingId: string, idempotencyKey: string): Promise<BookingBalance> {
  return apiPost(
    `/v1/business/bookings/${encodeURIComponent(bookingId)}/use-pack`,
    BookingBalanceSchema,
    undefined,
    idempotencyKey,
  );
}
